import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CreditCard, AlertCircle, CheckCircle, ExternalLink, WifiOff } from 'lucide-react';
import { stripeService } from '../services/stripeService';
import { toast } from 'sonner';

const StripeConnectBanner = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const s = await stripeService.getConnectStatus();
        setStatus(s);
        setErrored(false);
      } catch (e) {
        setStatus({ connected: false });
        setErrored(true);
      }
    })();
  }, []);

  const connect = async () => {
    setLoading(true);
    try {
      const returnUrl = `${window.location.origin}/mobilitier/dashboard?stripe=ok`;
      const refreshUrl = `${window.location.origin}/mobilitier/dashboard?stripe=retry`;
      const result = await stripeService.createConnectAccount(returnUrl, refreshUrl);
      window.location.href = result.onboarding_url;
    } catch (e) {
      toast.error('Error conectando con Stripe. Intenta de nuevo.');
      setLoading(false);
    }
  };

  if (!status) return null;

  // Backend error fallback - show banner so mobilitier knows something's wrong
  if (errored) {
    return (
      <Card className="bg-red-50 border-red-200 mb-6" data-testid="stripe-error-banner">
        <CardContent className="flex items-center gap-3 py-4">
          <WifiOff className="w-5 h-5 text-red-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900">No se pudo verificar tu cuenta Stripe</p>
            <p className="text-xs text-red-700">Recarga la página o contacta soporte si el problema persiste.</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => window.location.reload()} data-testid="stripe-retry-btn">
            Reintentar
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Fully connected & charges enabled
  if (status.connected && status.charges_enabled) {
    return (
      <Card className="bg-green-50 border-green-200 mb-6" data-testid="stripe-connected-banner">
        <CardContent className="flex items-center gap-3 py-4">
          <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-900">Cuenta Stripe conectada</p>
            <p className="text-xs text-green-700">Listo para recibir pagos automáticamente.</p>
          </div>
          <Badge className="bg-green-600">Verificado</Badge>
        </CardContent>
      </Card>
    );
  }

  // Connected but pending KYC
  if (status.connected && !status.charges_enabled) {
    return (
      <Card className="bg-yellow-50 border-yellow-300 mb-6" data-testid="stripe-pending-banner">
        <CardContent className="flex items-center gap-3 py-4">
          <AlertCircle className="w-5 h-5 text-yellow-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-900">Stripe pendiente de verificación</p>
            <p className="text-xs text-yellow-700">Completa el onboarding para empezar a cobrar.</p>
          </div>
          <Button size="sm" onClick={connect} disabled={loading} data-testid="stripe-resume-btn">
            <ExternalLink className="w-3 h-3 mr-1" /> Continuar
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Not connected at all
  return (
    <Card className="bg-blue-50 border-blue-300 mb-6" data-testid="stripe-disconnected-banner">
      <CardContent className="flex items-center gap-3 py-4">
        <CreditCard className="w-5 h-5 text-blue-600 shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-blue-900">Conecta tu cuenta Stripe para recibir pagos</p>
          <p className="text-xs text-blue-700">Sin esto, los renters no podrán reservar tus autos.</p>
        </div>
        <Button size="sm" onClick={connect} disabled={loading} className="bg-blue-600 hover:bg-blue-700" data-testid="stripe-connect-btn">
          {loading ? 'Conectando…' : 'Conectar Stripe'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default StripeConnectBanner;
